import { HttpVerb } from '../../types/api-types'
import { WellsAdminUserFieldsEdit } from '../../types/form-types'
import useEditForm from '../hooks/use-edit-form'
import { useSaveWellData } from '../hooks/use-api'
import WellEditButtons from './wells/well-edit-buttons'
import { FormEvent } from 'react'
import { APP_NAME } from '../../constants'
import { useNavigate } from 'react-router-dom'
import {
  ButtonsContainer,
  FormField,
  Heading,
  Icon,
  InlineNotification,
  Link,
  Modal,
  TextInput,
  useTitleChange,
} from '@msaf/core-react'

interface RegisterModalInterface {
  isOpen: boolean
  closeModal: () => void
}

export default function RegisterModal({ isOpen, closeModal }: RegisterModalInterface) {
  useTitleChange(APP_NAME)
  const saveMutationResult = useSaveWellData(`administration/user`, HttpVerb.POST)
  const { fields, isLoading, resetUseEditFormState, handleSave, isSaved, validationErrors } =
    useEditForm<WellsAdminUserFieldsEdit>({
      saveMutationResult,
      setIsDirty: (_isDirty: boolean) => {
        /** Form dirty check is not required here */
      },
    })
  const navigate = useNavigate()

  /**
   * `saveForm` is bound to the form `onSubmit` event so that we can
   * hit enter in the form. The `fields` are populated from the input
   * elements directly because the values in our input components
   * are updated `onChange` and that doesn't fire in the enter keypress
   * scenario OR in the case where the browser autofills the fields.
   */
  function saveForm(e: FormEvent) {
    e.preventDefault()

    const getInputValue = (selector: string) => {
      const formEl = e?.currentTarget
      const value = (formEl?.querySelector(selector) as HTMLInputElement)?.value
      return value ?? null
    }

    // Update the fields with the values of the inputs
    if (fields) {
      Object.assign(fields, {
        email: getInputValue('input#email'),
        firstName: getInputValue('input#firstName'),
        lastName: getInputValue('input#lastName'),
        organisation: getInputValue('input#organisation'),
        userType: 'Public',
      })
    }

    handleSave()
  }

  const handleCancel = () => {
    resetUseEditFormState()
    closeModal()
    navigate('/')
  }

  const SuccessComponent = () => (
    <>
      <ButtonsContainer containerStyle='right'>
        <Icon onClick={handleCancel} icon='close' ariaLabel='Close modal' />
      </ButtonsContainer>
      <InlineNotification messageType='success' isDismissable={false}>
        <Heading level={4}>Success</Heading>
        <p>
          You have been successfully added to Wells Aotearoa New Zealand.
          <br />
          Please check your email for a link to complete the registration process.
        </p>
        <p>Thank you.</p>
      </InlineNotification>
    </>
  )

  return (
    <>
      <Modal isOpen={isOpen} contentLabel='Register' onRequestClose={() => {}}>
        {isSaved ? (
          SuccessComponent()
        ) : (
          <>
            <Heading level={1}>Register</Heading>
            <form onSubmit={saveForm}>
              <FormField
                label='Email address'
                labelId='email-label'
                validationMessages={validationErrors?.email}
                isRequired
              >
                <TextInput
                  autoFocus={true}
                  isSkeleton={isLoading}
                  id='email'
                  value={fields.email ?? ''}
                  setValue={() => {}}
                />
              </FormField>
              <FormField
                label='First name'
                labelId='firstName-label'
                validationMessages={validationErrors?.firstName}
                isRequired
              >
                <TextInput isSkeleton={isLoading} id='firstName' value={fields.firstName ?? ''} setValue={() => {}} />
              </FormField>
              <FormField
                labelId='lastName-label'
                label='Last name'
                validationMessages={validationErrors?.lastName}
                isRequired
              >
                <TextInput isSkeleton={isLoading} id='lastName' value={fields.lastName ?? ''} setValue={() => {}} />
              </FormField>
              <FormField
                labelId='organisation-label'
                label='Organisation'
                validationMessages={validationErrors?.organisation}
              >
                <TextInput
                  isSkeleton={isLoading}
                  id='organisation'
                  value={fields.organisation ?? ''}
                  setValue={() => {}}
                />
              </FormField>
              <WellEditButtons
                actions={[
                  {
                    label: 'Submit',
                    type: 'submit',
                    buttonStyle: 'primary',
                    isDisabled: isLoading,
                  },
                  {
                    label: 'Cancel',
                    buttonStyle: 'text-action',
                    onClick: handleCancel,
                    isDisabled: isLoading,
                  },
                ]}
              />
            </form>
            <InlineNotification messageType='info' isDismissable={false}>
              This registration is for <strong>public users only. </strong>
              <br />
              <br />
              If you are a member of a <strong>drilling organisation</strong>, please{' '}
              <Link path='/contact-us'>contact a Council</Link> to ask about registration.
              <br />
              <br />
              If you are a member of a <strong>Regional or Unitary Council</strong>, please contact your Wells NZ
              administrator to ask about registration.
            </InlineNotification>
          </>
        )}
      </Modal>
    </>
  )
}
